<template>
  <div>
    <AgItem :columnDefs="columnDefs"
            :rowData="rowData"
            :with-tabs="true"
            :with-header="true"
            v-on:sortChanged="sortChanged"
    ></AgItem>
    <Pagination
            :totalNum="totalNum"
            :pageSize="pageSize"
            :sizeChange="sizeChange"
            :currentChange="currentChange"
            :currentPage="page"
    ></Pagination>
    <TransactionDetailsModal v-if="shouldShowAssignmentDetailsModal"
                            :auctionId = "auctionId"
                            @close="closeAssignmentDetailsModal"
    ></TransactionDetailsModal>
  </div>
</template>
<script>
import AgItem from "@/components/templates/agGrid/AgItem";
import CompanyTem from "@/components/templates/agGrid/CompanyTem";
import ItemDetail from "@/components/templates/agGrid/ItemDetail";
import {
	asDecimal,
	asLocalizedDate,
	asMoneyAmount,
	asPercentage
} from "@/components/templates/agGrid/formatter/valueFormatters";
import {allReceivablesApi} from "@/common/api/allReceivablesApi";
import Pagination from "@/components/templates/common/Pagination";
import PaginationParent from "@/components/templates/common/PaginationParent";
import TransactionDetailsModal from "@/components/templates/seller/receivables/TransactionDetailsModal";

export default {
  extends: PaginationParent,
  components: {
    AgItem,
    Pagination,
    TransactionDetailsModal
  },
  props: {
    filters: { type: Array, default: [] }
  },
  data() {
    return {
      columnDefs: [],
      rowData: null,
      shouldShowAssignmentDetailsModal: false,
      auctionId: null,
      sorting: []
    };
  },
  beforeMount() {
    this.setColumnDefs();
    this.getList();
  },
  methods: {
    setColumnDefs() {
      this.columnDefs = [
        {
          headerName: this.$t("common.receivableDetails"),
          children: [
            {
              headerName: this.$t("common.status"),
              field: "receivableState",
              cellRendererFramework: "AllReceivablesStateRenderer",
              cellRendererParams: {
                showModal: this.showAssignmentDetailsModal
              },
              minWidth: 100,
            },
            {
              headerName: this.$t("common.date"),
              minWidth: 100,
              field: "stateDate",
              valueFormatter: asLocalizedDate(),
              sortable: true,
            },
            {
              headerName: this.$t("common.debtor"),
              field: "debtorName",
              enableValue: true,
              suppressMenu: true,
              sortable: true,
              nameType: "debtorName",
              idType: "debtorId",
              cellRendererFramework: "CompanyTem",
              minWidth:200
            },
            {
              headerName: this.$t("common.invoiceNumber"),
              field: "invoiceNumber",
              minWidth: 175,
              cellRendererFramework: "ItemDetail",
              sortable: true,
            },
            {
              headerName: this.$t("common.amount"),
              headerClass: "align-right",
              field: "amount",
              cellStyle: {'justify-content': 'flex-end'},
              valueFormatter: asMoneyAmount(),
              sortable: true,
              minWidth: 110,
            },
            {
              headerName: this.$t("common.ccy"),
              minWidth: 70,
              maxWidth: 100,
              field: "currency",
              sortable: true,
            },
            {
              headerName: this.$t("common.dueDate"),
              field: "dueDate",
              minWidth: 120,
              valueFormatter: asLocalizedDate(),
              cellRendererFramework: "OverdueRenderer",
              cellRendererParams: {
              	auctionStatusField: "auctionStatus",
              	receivableStatusField: "receivableStatus",
              },
              sortable: true,
            }
          ]
        },
        {
          headerName: this.$t("common.assignment"),
          headerGroupComponentParams: {
            tooltip: this.$t("seller.receivables.table.header.assignment.tooltip"),
          },
          children: [
            {
              headerName: this.$t("common.transactionCode"),
              headerComponentParams: {
                tooltip: this.$t("seller.receivables.table.header.transactionCode.tooltip"),
              },
              field: "transactionCode",
              minWidth: 145,
              cellRendererFramework: "AssignmentDetailsRenderer",
              cellRendererParams: {
                showModal: this.showAssignmentDetailsModal
              },
              sortable: true,
            },
            {
              headerName: this.$t("common.investor"),
              field: "buyerName",
              nameType: "buyerName",
              idType: "buyerId",
              cellRendererFramework: "CompanyTem",
              sortable: true,
              minWidth:200
            },
            {
              headerName: this.$t("common.retention"),
              headerClass: "align-right",
              field: "retention",
              cellStyle: {'justify-content': 'flex-end'},
              minWidth: 100,
              valueFormatter: asPercentage({default: "0", decimals: 0 }),
              sortable: true
            },
            {
              headerName: this.$t("common.price"),
              headerClass: "align-right",
              field: "price",
              cellStyle: {'justify-content': 'flex-end'},
              valueFormatter: asMoneyAmount(),
              sortable: true,
              minWidth: 110,
            }
          ]
        },
        {
          headerName: this.$t("seller.receivables.table.header.repayments"),
          headerGroupComponentParams: {
            tooltip: this.$t("seller.receivables.table.header.repayments.tooltip"),
          },
          children: [
            {
              headerName: this.$t("common.outstanding"),
              headerComponentParams: {
                tooltip: this.$t("seller.receivables.table.header.outstanding.tooltip"),
              },
              headerClass: "align-right",
              field: "outstandingPercent",
              cellStyle: {'justify-content': 'flex-end'},
              valueFormatter: asDecimal({showPercentSign: true}),
              sortable: true,
              minWidth: 100
            },
            {
              headerName: this.$t("common.latePaymentDays"),
              headerComponentParams: {
                tooltip: this.$t("seller.receivables.table.header.latePaymentDays.tooltip"),
              },
              field: "latePaymentDays",
              sortable: true,
              minWidth: 90,
              maxWidth: 90
            }
          ]
        },
        {
          headerName: this.$t("common.actions"),
          children: [
            {
              minWidth: 100,
              cellRendererFramework: "AllReceivablesTableOperation",
              cellRendererParams: {
                downloadDocuments: this.downloadDocuments
              },
              pinned: "right"
            }
          ]
        }
      ];
    },
    getList() {
      let postData = {
        columns: [],
        filters: this.filters || [],
        maxResults: this.maxResults,
        offset: this.offset,
        sorting: this.sorting
      };
      allReceivablesApi
        .list(postData)
        .then(res => {
          this.setTotalNum(res.recordsTotal);
          this.rowData = res.data;
        })
        .catch(err => {
          this.toast(err);
        });
    },
    sortChanged(sortData) {
      this.sorting = sortData;
      this.initPage();
      this.getList();
    },
    downloadDocuments(data) {
      let postData = {
        auctionId: data.auctionId,
        needAssignment: true,
        needAdviceOfSettlement: true,
        needServiceInvoice: true,
        needTransactionDocument: true,
        needCsv: true,
        forSell: true
      };

      allReceivablesApi.downloadAllDocuments(postData)
          .then(res => {
            this.toastSuccess(this.$t("common.download.allDocuments.success.msg"));
            window.open(res.msg, '_blank')
          })
          .catch(e => {
            this.toast(e);
          });
    },
    showAssignmentDetailsModal(id) {
      this.shouldShowAssignmentDetailsModal = true;
      this.auctionId = id;
    },
    closeAssignmentDetailsModal() {
      this.shouldShowAssignmentDetailsModal = false;
    },
  },
  watch: {
    filters() {
      this.getList();
    }
  }
};
</script>

